// File: src/pages/dashboard/ArtistDashboard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { useProfile } from '../../context/ProfileContext';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedPageWrapper from '../../components/miscellaneous/AnimatedPageWrapper';

// Tab imports (EarningsTab removed)
import AudienceDashboard from './tabs/AudienceDashboard';
import MarketingInsightsTab from './tabs/MarketingInsightsTab';

// Points and Charting Components
import ArtistMonthlyPointsChart from '../../components/artistDashboard/ArtistMonthlyPointsChart';
import ArtistChartingSingleCard from '../../components/artistDashboard/ArtistChartingSingleCard';

// 🌟 1. Removed 'earnings' from NAVIGATION_TABS
const NAVIGATION_TABS = [
    { key: 'summary', label: 'Account Overview' },
    { key: 'track_stats', label: 'Track Stats' },
    { key: 'fan_workshop', label: 'Fan Workshop' }
];

const ArtistDashboard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { activeProfile, isDarkMode } = useProfile();
    const db = getFirestore();

    const activeTab = searchParams.get('tab') || 'summary';
    const [rankingMetric, setRankingMetric] = useState('streams');
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const [summaryData, setSummaryData] = useState(null);
    const [songListData, setSongListData] = useState(null);
    const [artistPosts, setArtistPosts] = useState([]);

    const handleSelectTab = (tabKey) => {
        setSearchParams({ tab: tabKey }, { replace: true });
        setIsMenuOpen(false);
    };

    const fetchArtistPosts = useCallback(async (artistId) => {
        if (!artistId) return;
        try {
            const postsRef = collection(db, "artistPosts");
            const q = query(
                postsRef,
                where("artistId", "==", artistId),
                where("deletedAt", "==", null),
                orderBy("createdAt", "desc")
            );
            const querySnapshot = await getDocs(q);
            const posts = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
            setArtistPosts(posts);
        } catch (error) {
            console.error("Error fetching artist posts:", error);
        }
    }, [db]);

    useEffect(() => {
        const artistId = activeProfile?.id;
        if (activeProfile?.type !== 'artist' || !artistId) return;

        const cacheKeySummary = `atomic_summary_${artistId}`;
        const cacheKeySongs = `atomic_songs_${artistId}`;

        const cachedSummary = sessionStorage.getItem(cacheKeySummary);
        const cachedSongs = sessionStorage.getItem(cacheKeySongs);

        if (cachedSummary && cachedSongs) {
            try {
                setSummaryData(JSON.parse(cachedSummary));
                setSongListData(JSON.parse(cachedSongs));
            } catch (e) {
                console.error("Error parsing session storage cache:", e);
                sessionStorage.removeItem(cacheKeySummary);
                sessionStorage.removeItem(cacheKeySongs);
            }
        } else {
            const functionsInstance = getFunctions();
            const getArtistSummaryCall = httpsCallable(functionsInstance, 'getArtistSummary');
            const getArtistSongsDetailCall = httpsCallable(functionsInstance, 'getArtistSongsDetail');

            getArtistSummaryCall({ artistId })
                .then(response => {
                    setSummaryData(response.data);
                    sessionStorage.setItem(cacheKeySummary, JSON.stringify(response.data));
                })
                .catch(err => {
                    console.error("Error fetching summary:", err);
                    setSummaryData(null);
                });

            getArtistSongsDetailCall({ artistId })
                .then(response => {
                    setSongListData(response.data);
                    sessionStorage.setItem(cacheKeySongs, JSON.stringify(response.data));
                })
                .catch(err => {
                    console.error("Error fetching song details:", err);
                    setSongListData(null);
                });
        }
        
        fetchArtistPosts(artistId);
    }, [activeProfile?.id, activeProfile?.type, fetchArtistPosts]);

    const processedRankedSongs = useMemo(() => {
        if (!songListData?.songs) return [];

        const normalizedSongs = songListData.songs.map(song => {
            let peakPosition = song.lastPosition || null;
            if (song.chartHistory && typeof song.chartHistory === 'object') {
                const positions = Object.values(song.chartHistory).filter(pos => typeof pos === 'number');
                if (positions.length > 0) {
                    peakPosition = Math.min(...positions);
                }
            }

            return {
                ...song,
                allTimeStreams: song.stats?.allTimeStreams || 0,
                totalChartPoints: song.stats?.totalChartPoints || 0,
                downloadsCount: song.downloads || 0,
                peakPosition: peakPosition,
                weeksOnChart: song.totalWeeksOnChart || 0
            };
        });

        if (rankingMetric === 'streams') {
            return normalizedSongs.sort((a, b) => b.allTimeStreams - a.allTimeStreams);
        } else {
            return normalizedSongs.sort((a, b) => {
                const posA = a.peakPosition || Number.MAX_SAFE_INTEGER;
                const posB = b.peakPosition || Number.MAX_SAFE_INTEGER;
                return posA - posB;
            });
        }
    }, [songListData, rankingMetric]);

    return (
        <AnimatedPageWrapper direction="right">
            {/* Root Page Wrapper */}
            <div className={`h-full w-full p-4 pt-14 overflow-y-auto pb-28 transition-colors duration-500 ${
                isDarkMode ? "bg-zinc-950 text-zinc-100" : "bg-gray-50 text-gray-900"
            }`}>
                
                {/* Centered container with responsive max-width */}
                <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 items-start">
                    
                    {/* LEFT / MAIN COLUMN */}
                    <div className="flex-1 w-full min-w-0">
                        {/* Header Row */}
                        <div className="flex justify-between items-center mb-2.5">
                            <div>
                                <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-brand-pink to-purple-500 bg-clip-text text-transparent">
                                    Dashboard
                                </h1>
                                <p className="text-xs sm:text-sm opacity-60">
                                    {activeTab === 'summary' ? 'Account Overview' : activeTab.replace('_', ' ')}
                                </p>
                            </div>

                            {/* Mobile-Only Hamburger Button (hidden on desktop) */}
                            <button 
                                onClick={() => setIsMenuOpen(true)}
                                className={`lg:hidden p-2 rounded-xl border transition-all ${
                                    isDarkMode 
                                        ? "bg-zinc-900 border-zinc-850 text-white hover:bg-zinc-800" 
                                        : "bg-white border-gray-200 text-gray-800 hover:bg-gray-50"
                                }`}
                                aria-label="Open Navigation Menu"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
                                </svg>
                            </button>
                        </div>

                        {/* Tab Content Area */}
                        <div>
                            {activeTab === 'summary' && (
                                <div className="space-y-4">
                                    {/* Monthly Points Performance Chart */}
                                    <ArtistMonthlyPointsChart 
                                        artistId={activeProfile?.id}
                                        summaryData={summaryData}
                                        isDarkMode={isDarkMode}
                                    />

                                    {/* Charting Single Spotlight Card */}
                                    <ArtistChartingSingleCard 
                                        artistId={activeProfile?.id}
                                        songs={processedRankedSongs}
                                        summaryData={summaryData}
                                        isDarkMode={isDarkMode}
                                    />

                                    {/* Track Rankings Section */}
                                    <div>
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-y-3">
                                            <h2 className="text-xl font-extrabold">Track Rankings</h2>

                                            {/* Sliding Toggle Selector */}
                                            <div className={`p-1 flex rounded-full relative w-full sm:w-[280px] h-10 border ${
                                                isDarkMode ? "bg-zinc-900 border-zinc-850" : "bg-gray-200 border-gray-300"
                                            }`}>
                                                <div className="flex w-full relative z-10">
                                                    <button 
                                                        onClick={() => setRankingMetric('streams')}
                                                        className={`flex-1 text-center text-xs font-extrabold h-full transition-colors duration-200 ${
                                                            rankingMetric === 'streams' 
                                                                ? "text-white" 
                                                                : (isDarkMode ? "text-zinc-500" : "text-gray-600")
                                                        }`}
                                                    >
                                                        Top Streams
                                                    </button>
                                                    
                                                    <button 
                                                        onClick={() => setRankingMetric('charting')}
                                                        className={`flex-1 text-center text-xs font-extrabold h-full transition-colors duration-200 ${
                                                            rankingMetric === 'charting' 
                                                                ? "text-white" 
                                                                : (isDarkMode ? "text-zinc-500" : "text-gray-600")
                                                        }`}
                                                    >
                                                        Chart Standings
                                                    </button>
                                                </div>
                                                
                                                <motion.div 
                                                    animate={{ x: rankingMetric === 'streams' ? 0 : '100%' }}
                                                    transition={{ type: "spring", stiffness: 300, damping: 28 }}
                                                    className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] bg-brand-pink rounded-full z-0 shadow-lg shadow-brand-pink/30"
                                                />
                                            </div>  
                                        </div>

                                        {!songListData ? (
                                            <div className="animate-pulse space-y-3">
                                                {[1, 2, 3].map(n => (
                                                    <div key={n} className={`h-16 rounded-xl ${isDarkMode ? 'bg-zinc-900' : 'bg-gray-200'}`} />
                                                ))}
                                            </div>
                                        ) : processedRankedSongs.length === 0 ? (
                                            <p className="text-sm opacity-50">No catalog track data found.</p>
                                        ) : (
                                            <div className="grid gap-2.5">
                                                {processedRankedSongs.slice(0, 5).map((song, idx) => (
                                                    <div 
                                                        key={song.id || idx} 
                                                        className={`p-3 rounded-xl flex items-center justify-between gap-2.5 border transition-all ${
                                                            isDarkMode ? "bg-zinc-900 border-zinc-850 hover:bg-zinc-850" : "bg-white border-gray-150 hover:bg-gray-50"
                                                        }`}
                                                    >
                                                        <div className="flex items-center space-x-2.5 min-w-0 flex-grow">
                                                            <span className="text-sm font-black text-brand-pink w-6 flex-shrink-0 text-center">#{idx + 1}</span>
                                                            
                                                            {song.albumArtworkURL && (
                                                                <img 
                                                                    src={song.albumArtworkURL} 
                                                                    alt={song.title} 
                                                                    className="w-11 h-11 rounded-xl object-cover bg-zinc-800 flex-shrink-0 border border-black/5"
                                                                />
                                                            )}
                                                            
                                                            <div className="min-w-0">
                                                                <h4 className="font-extrabold text-xs truncate leading-tight">{song.title}</h4>
                                                                <p className="text-[10px] opacity-50 mt-0.5">
                                                                    Streams: {song.allTimeStreams.toLocaleString()}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="flex flex-col items-end space-y-1 flex-shrink-0">
                                                            {song.peakPosition ? (
                                                                <span className="text-[8px] sm:text-[9px] font-black uppercase tracking-wider bg-brand-pink text-white px-2 py-0.5 rounded-full whitespace-nowrap shadow-sm shadow-brand-pink/15">
                                                                    Peak #{song.peakPosition}
                                                                </span>
                                                            ) : (
                                                                <span className="text-[8px] sm:text-[9px] font-black uppercase tracking-wider bg-zinc-500/10 text-zinc-500 dark:text-zinc-400 px-2 py-0.5 rounded-full whitespace-nowrap">
                                                                    Uncharted
                                                                </span>
                                                            )}

                                                            {song.weeksOnChart > 0 && (
                                                                <span className={`text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border whitespace-nowrap ${
                                                                    isDarkMode 
                                                                        ? "bg-zinc-800/50 text-zinc-400 border-zinc-750" 
                                                                        : "bg-gray-100 text-gray-500 border-gray-200"
                                                                }`}>
                                                                    {song.weeksOnChart} {song.weeksOnChart === 1 ? 'Week' : 'Weeks'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === 'track_stats' && (
                                (!summaryData || !songListData)
                                    ? <p className="opacity-50 text-sm animate-pulse">Retrieving audience analytics...</p>
                                    : <AudienceDashboard summaryData={summaryData} monthKeys={songListData.last12MonthKeys} songListData={songListData} />
                            )}

                            {activeTab === 'fan_workshop' && (
                                <MarketingInsightsTab summaryData={summaryData} />
                            )}
                        </div>
                    </div>

                    {/* RIGHT COLUMN: PERMANENT SIDEBAR ON DESKTOP */}
                    <div className="hidden lg:block w-64 flex-shrink-0 sticky top-14">
                        <div className={`p-4 rounded-2xl border shadow-sm ${
                            isDarkMode ? "bg-zinc-900 border-zinc-800 text-white" : "bg-white border-gray-200 text-gray-900"
                        }`}>
                            <div className="mb-4 pb-2 border-b border-zinc-800/50">
                                <span className="font-black text-xs tracking-wider uppercase text-brand-pink">
                                    Dashboard Menu
                                </span>
                            </div>

                            <div className="flex flex-col space-y-1.5">
                                {NAVIGATION_TABS.map(item => (
                                    <button
                                        key={item.key}
                                        onClick={() => handleSelectTab(item.key)}
                                        className={`text-left px-3.5 py-2.5 rounded-xl font-bold transition-all text-xs ${
                                            activeTab === item.key 
                                                ? "bg-brand-pink text-white shadow-md shadow-brand-pink/20 scale-[1.01]" 
                                                : isDarkMode 
                                                    ? "hover:bg-zinc-800 text-zinc-400 hover:text-white" 
                                                    : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                                        }`}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>

                {/* MOBILE NAVIGATION DRAWER */}
                <AnimatePresence>
                    {isMenuOpen && (
                        <div className="lg:hidden">
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 0.6 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setIsMenuOpen(false)}
                                className="fixed inset-0 bg-black z-[100]"
                            />

                            <motion.div 
                                initial={{ x: "100%" }}
                                animate={{ x: 0 }}
                                exit={{ x: "100%" }}
                                transition={{ type: "spring", damping: 25, stiffness: 220 }}
                                className={`fixed top-0 right-0 bottom-0 w-[280px] max-w-full shadow-2xl z-[110] flex flex-col p-6 ${
                                    isDarkMode ? "bg-zinc-900 border-l border-zinc-800 text-white" : "bg-white text-gray-900"
                                }`}
                            >
                                <div className="flex justify-between items-center mb-8">
                                    <span className="font-black text-lg tracking-tight uppercase text-brand-pink">Navigation</span>
                                    <button 
                                        onClick={() => setIsMenuOpen(false)}
                                        className={`p-1.5 rounded-lg border ${
                                            isDarkMode ? "border-zinc-800 text-zinc-400" : "border-gray-200 text-gray-500"
                                        }`}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="flex flex-col space-y-2 flex-grow">
                                    {NAVIGATION_TABS.map(item => (
                                        <button
                                            key={item.key}
                                            onClick={() => handleSelectTab(item.key)}
                                            className={`text-left p-4 rounded-xl font-bold transition-all text-sm ${
                                                activeTab === item.key 
                                                    ? "bg-brand-pink text-white shadow-lg shadow-brand-pink/20 scale-[1.02]" 
                                                    : isDarkMode 
                                                        ? "hover:bg-zinc-850 text-zinc-400 hover:text-white" 
                                                        : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                                            }`}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>

            </div>
        </AnimatedPageWrapper>
    );
};

export default ArtistDashboard;